import moment from 'moment';
import { map, slice, forEach, concat, merge } from 'lodash';

import { prettifyNumber } from 'application/util/number.util';
import { getErrorMessage } from 'application/util/error.util';

import './cash-flow-chart.style.sass';
import template from './cash-flow-chart.tpl.pug';

class CashFlowChartController {
    /* @ngInject */
    constructor(dashboardService) {
        this.dashboardService = dashboardService;
        
        this.options = this.getChartOptions();
    }

    $onInit() {
        this.loading = true;
    }

    $onChanges(changeObject) {
        if (changeObject.calendarIds || changeObject.start || changeObject.end) {
            return this.load(
                this.calendarIds,
                moment(this.start).startOf('day').valueOf(),
                moment(this.end).endOf('day').valueOf()
            );
        }
    }

    getTooltipLabel(item, data) {
        let index = item['index'];
        let datasetIndex = item.datasetIndex;

        // skip the common point having 2 balances in the tooltip
        if (datasetIndex === 1 && data.datasets[0].data.length > index) {
            return null;
        }

        let balance = prettifyNumber(data.datasets[datasetIndex].data[index], {
            preventShrink: true,
            pretty: true
        });

        let change = prettifyNumber(data.datasets[datasetIndex].changes[index], {
            preventShrink: true,
            addPositiveSign: true,
            pretty: true
        });

        return [
            datasetIndex === 0
                ? `Balance`
                : `Forecasted balance`,
            balance,
            `Change`,
            change
        ];
    }

    getBalanceTick(label) {
        return prettifyNumber(label);
    }

    getUnitFromDataCount(count) {
        return count > 1000
            ? 'year'
            : count > 70
                ? 'month'
                : 'day';
    }

    getChartOptions() {
        return {
            layout: {
                padding: {
                    left: 5,
                    right: 15,
                    bottom: 40
                }
            },
            tooltips: {
                callbacks: {
                    label: this.getTooltipLabel.bind(this)
                }
            },
            scales: {
                yAxes: [{
                    gridLines: {
                        color: '#ebeced',
                        borderDash: [3, 5],
                        zeroLineColor: '#ebeced',
                        zeroLineBorderDash: [3, 5],
                        drawBorder: false,
                        drawTicks: false
                    },
                    ticks: {
                        padding: 30,
                        suggestedMin: 0,
                        suggestedMax: 0,
                        beginAtZero: true,
                        fontFamily: `'Open Sans', sans-serif`,
                        callback: this.getBalanceTick.bind(this)
                    }
                }],
                xAxes: [{
                    type: 'time',
                    time: {
                        unit: 'day',
                        tooltipFormat: 'dddd, MMM D, YYYY'
                    },
                    gridLines: {
                        color: '#ebeced',
                        drawBorder: false,
                        drawTicks: false,
                    },
                    ticks: {
                        maxTicksLimit: 12,
                        autoSkipPadding: 20,
                        padding: 15,
                        maxRotation: 0,
                        minRotation: 0,
                        fontFamily: `'Open Sans', sans-serif`
                    }
                }]
            }
        };
    }

    load(calendarIds, start, end) {
        start = moment(start);
        end = moment(end);

        this.loading = true;

        let today = moment().startOf('day').valueOf();
        let labels = [];
        let past = [];
        let future = [];
        let changes = [];

        let periodStart = start.isValid()
            ? moment(start).valueOf()
            : null;

        return this.dashboardService.getCashFlowData(calendarIds, periodStart, end.valueOf()).then(({ dataSet, meta }) => {
            forEach(dataSet, (day) => {
                labels.push(day.timestamp);
                changes.push(day.change);

                if (day.timestamp <= today) {
                    past.push(day.value);
                } else {
                    future.push(day.value);
                }
            });

            if (past.length > 0) {
                let emptyPast = map(slice(past, 0, past.length - 1), () => null);
                future = concat(concat(emptyPast, [past[past.length - 1]]), future);
            }

            this.updateMeta(meta.income, meta.expense, dataSet.length);

            this.error = null;
        }).catch((result) => {
            this.updateMeta();

            this.error = getErrorMessage(result, `Unfortunately we couldn't load the chart data. Please try again later.`);
        }).finally(() => {
            this.updateScaleByDataCount(labels.length);

            this.data = {
                labels,
                datasets: [
                    this.getDataSet(past, changes, true),
                    this.getDataSet(future, changes)
                ]
            };

            this.loading = false;
        });
    }

    updateScaleByDataCount(count) {
        let options = merge({}, this.options);
        
        options.scales.xAxes[0].time = merge({}, options.scales.xAxes[0].time, {
            unit: this.getUnitFromDataCount(count),
            displayFormats: {
                month: count > 30 * 6
                    ? 'MMM'
                    : 'MMMM',
                day: 'D'
            }
        });

        this.options = options;

        return this.options;
    }

    updateMeta(income, expense, count) {
        this.income = income || 0;
        this.expense = expense || 0;

        this.change = this.income - this.expense;
        this.average = this.expense > 0
            ? count > 0
                ? this.expense / count
                : 0
            : 0;
    }

    getCssVar(name, fallback) {
        const value = getComputedStyle(document.documentElement).getPropertyValue(name);

        return value && value.trim()
            ? value.trim()
            : fallback;
    }

    getDataSet(data, changes, isPast) {
        const pastFill = this.getCssVar('--color-chart-area-past', '#cfeafb');
        const futureFill = this.getCssVar('--color-chart-area-future', '#e7f4fd');

        return {
            data,
            changes,
            backgroundColor: isPast
                ? pastFill
                : futureFill,
            borderDash: isPast
                ? null
                : [5, 5],
            borderDashOffset: 5,
            borderColor: isPast
                ? '#1196ea'
                : '#88caf4',
            borderWidth: 2,
            lineTension: 0,
            pointHitRadius: 15,
            pointRadius: 0,
            pointBorderWidth: 0,
            pointHoverBackgroundColor: isPast
                ? '#1196ea'
                : '#88caf4',
            pointHoverBorderColor: '#fff',
            pointHoverRadius: 5,
            pointHoverBorderWidth: 2,
        };
    }
}

export default {
    selector: 'dbACashFlowChart',
    template: template(),
    controller: CashFlowChartController,
    bindings: {
        calendarIds: '<',
        start: '<',
        end: '<'
    }
};



// WEBPACK FOOTER //
// ./src/application/scene/authorized/scene/dashboard/component/cash-flow-chart/cash-flow-chart.cmpt.js